import { useEffect, useState } from "react";
import {Button, Card, Col, Form, Input, Row, Select, Space, Switch} from "antd";
import DatePicker from "../../core/components/DatePicker";
import { SearchOutlined } from "@ant-design/icons";
import { t } from "i18next";
import { useDataGrid } from "../../core/components/data-grid/DataGridContext";
import Helper from "../../core/Helper";
import ApiService from "../../core/service/ApiService";
import FirmSelectList from "../firm/FirmSelectList";

export default function EnabizOperationFilter() {
    const { onFilterPageSort, queryModel } = useDataGrid();
    const [form] = Form.useForm();

    const [units, setUnits] = useState([]);
    const [institutions, setInstitutions] = useState([]);

    useEffect(() => {
        form.setFieldsValue(queryModel.filter)
    }, [queryModel.updateKey])

    useEffect(() => {
        ApiService.Unit.GetUnitList().then(res => {
            if (res.success) {
                setUnits(res.data.map(x => ({ ...x, name: `${x?.firm?.name || ''} - ${x.name}` })))
            }
        })
        ApiService.Institution.GetInstitutionList(true).then(res => {
            if (res.success) setInstitutions(res.data)
        })
    }, [])

    const onFinish = (formData) => {
        onFilterPageSort({ filter: formData })
    }

    const colProps = { xs: 24, sm: 12, md: 8, lg: 6, xl: 4 }
    const compactStyle = { marginBottom: 8 }

    return (
        <Card size="small" style={{ marginBottom: 12, backgroundColor: '#fafafa' }}>
            <Form form={form} size={'small'} layout={'vertical'} onFinish={onFinish}>
                <Row gutter={[12, 0]}>
                    <Col {...colProps}>
                        <Form.Item name={'startDate'} label={t('Start Date')} style={compactStyle}>
                            <DatePicker style={{ width: '100%' }} format={'DD/MM/yyyy'} locale={Helper.Date.getLocale()} />
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'endDate'} label={t('End Date')} style={compactStyle}>
                            <DatePicker style={{ width: '100%' }} format={'DD/MM/yyyy'} locale={Helper.Date.getLocale()} />
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item label={t('Quick Date')} style={compactStyle}>
                            <Select
                                placeholder={t('Select')}
                                style={{ width: '100%' }}
                                onSelect={(val) => {
                                    const dates = Helper.Date.getDateFromNow(val);
                                    form.setFieldsValue({ startDate: dates.start, endDate: dates.end })
                                }}
                            >
                                <Select.Option value={'Today'}>{t('Today')}</Select.Option>
                                <Select.Option value={'Yesterday'}>{t('Yesterday')}</Select.Option>
                                <Select.Option value={'This Week'}>{t('This Week')}</Select.Option>
                                <Select.Option value={'This Month'}>{t('This Month')}</Select.Option>
                                <Select.Option value={'Last 7 Days'}>{t('Last 7 Days')}</Select.Option>
                                <Select.Option value={'Last 30 Days'}>{t('Last 30 Days')}</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'firmIdList'} label={t('Firm')} style={compactStyle}>
                            <FirmSelectList style={{ width: '100%' }} allowClear mode={'multiple'} />
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'unitIdList'} label={t('Unit')} style={compactStyle}>
                            <Select allowClear style={{ width: '100%' }} showSearch mode={'multiple'}
                                    fieldNames={{ label: 'name', value: 'id' }}
                                    options={units}
                                    filterOption={(input, option) => Helper.Text.normalizeText(JSON.stringify(option)).includes(Helper.Text.normalizeText(input))} />
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'institutionId'} label={t('Institution')} style={compactStyle}>
                            <Select allowClear showSearch style={{ width: '100%' }}
                                    fieldNames={{ label: 'name', value: 'id' }}
                                    options={institutions}
                                    filterOption={(input, option) => Helper.Text.normalizeText(JSON.stringify(option)).includes(Helper.Text.normalizeText(input))} />
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'patientCustomId'} label={t('Patient Id')} style={compactStyle}>
                            <Input allowClear />
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'patientName'} label={t('Patient Name')} style={compactStyle}>
                            <Input allowClear />
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'appointmentId'} label={t('Appointment Id')} style={compactStyle}>
                            <Input allowClear />
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'accessionNumber'} label={t('Accession No')} style={compactStyle}>
                            <Input allowClear />
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'operationName'} label={t('Operation')} style={compactStyle}>
                            <Input allowClear />
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'stateGroupIdList'} label={t('Appointment State')} style={compactStyle}>
                            <Select allowClear mode={'multiple'} placeholder={t('All')} style={{ width: '100%' }}>
                                <Select.Option value={2}>{t('Completed')}</Select.Option>
                                <Select.Option value={1}>{t('Open')}</Select.Option>
                                <Select.Option value={3}>{t('Cancelled')}</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'risHl7Status'} label={t('RIS HL7 Status')} style={compactStyle}>
                            <Select allowClear placeholder={t('All')} style={{ width: '100%' }}>
                                <Select.Option value={'sent'}>{t('Sent')}</Select.Option>
                                <Select.Option value={'notsent'}>{t('Not Sent')}</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        {/* Eskiden "Failed / Not Sent" tek seçenekti; hiç denenmemiş kayıtlarla
                            gönderilirken hata alanlar aynı kovaya düşüyordu. Artık üç ayrı durum. */}
                        <Form.Item name={'risJobSendStatus'} label={t('KOS Send Status')} style={compactStyle}>
                            <Select allowClear placeholder={t('All')} style={{ width: '100%' }}>
                                <Select.Option value={'success'}>{t('Sent')}</Select.Option>
                                <Select.Option value={'fail'}>{t('Send Failed')}</Select.Option>
                                <Select.Option value={'notsent'}>{t('Not Sent')}</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'teletipMatchStateList'} label={t('Image Match')} style={compactStyle}>
                            <Select allowClear mode={'multiple'} placeholder={t('All')} style={{ width: '100%' }}>
                                <Select.Option value={2}>{t('Image Matched')}</Select.Option>
                                <Select.Option value={1}>{t('Waiting For Image')}</Select.Option>
                                <Select.Option value={3}>{t('No Record In Teletip')}</Select.Option>
                                <Select.Option value={4}>{t('Match Failed')}</Select.Option>
                                <Select.Option value={5}>{t('Match Timed Out')}</Select.Option>
                                <Select.Option value={6}>{t('Could Not Query')}</Select.Option>
                                <Select.Option value={0}>{t('Not Queried')}</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'risJobStateList'} label={t('RIS Job State')} style={compactStyle}>
                            <Select allowClear mode={'multiple'} placeholder={t('All')} style={{ width: '100%' }}>
                                <Select.Option value={1}>{t('RIS Job Completed')}</Select.Option>
                                <Select.Option value={0}>{t('RIS Job Pending')}</Select.Option>
                                <Select.Option value={3}>{t('RIS Job Waiting Study')}</Select.Option>
                                <Select.Option value={2}>{t('RIS Job Error')}</Select.Option>
                                <Select.Option value={4}>{t('RIS Job Expired')}</Select.Option>
                                <Select.Option value={99}>{t('RIS Job Unknown')}</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'requestCancelStatus'} label={t('Request Cancellation')} style={compactStyle}>
                            <Select allowClear placeholder={t('All')} style={{ width: '100%' }}>
                                <Select.Option value={'cancelled'}>{t('Cancelled')}</Select.Option>
                                <Select.Option value={'failed'}>{t('Cancel Failed')}</Select.Option>
                                <Select.Option value={'notcancelled'}>{t('Not Cancelled')}</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'onlyProblems'} label={t('Only Problems')} valuePropName={'checked'} style={compactStyle}>
                            <Switch size={'small'} />
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'requestHl7Status'} label={t('Request HL7 Status')} style={compactStyle}>
                            <Select allowClear placeholder={t('All')} style={{ width: '100%' }}>
                                <Select.Option value={'success'}>{t('Success')}</Select.Option>
                                <Select.Option value={'fail'}>{t('Failed')}</Select.Option>
                                <Select.Option value={'none'}>{t('Not Sent')}</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col {...colProps}>
                        <Form.Item name={'reportHl7Status'} label={t('Report HL7 Status')} style={compactStyle}>
                            <Select allowClear placeholder={t('All')} style={{ width: '100%' }}>
                                <Select.Option value={'success'}>{t('Success')}</Select.Option>
                                <Select.Option value={'fail'}>{t('Failed')}</Select.Option>
                                <Select.Option value={'none'}>{t('Not Sent')}</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8} lg={6} xl={4} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 8 }}>
                        <Space>
                            <Button type="primary" htmlType={'submit'} icon={<SearchOutlined />}>
                                {t('Search')}
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Form>
        </Card>
    )
}
